/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/FeaturedCompanies/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./index.module.css";

import { CircularProgress, Backdrop } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { ReactComponent as MedalIcon } from "../../assets/icons/FeaturedSectors/Medal.svg";
import FeaturedCompanyBanner from "../../assets/featuredCompanyBanner.png";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { getFeaturedCompanies } from "../../logic/featuredCompanies";
import { useTranslation } from "react-i18next";
import AssetImage from "../AssetImage";

const FeaturedCompanies = ({ handleCardClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [featuredCompanies, setFeaturedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError(null);
        const companies = await getFeaturedCompanies(navigate);
        setFeaturedCompanies(Array.isArray(companies) ? companies : []);
      } catch (fetchError) {
        logger.error("Error fetching featured companies:", {
          err: sanitizeError(fetchError),
        });
        setError(t("components.featuredSections.errorLoadingCompanies"));
        setFeaturedCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [navigate]);

  const handleCompanySearch = (company) => {
    handleCardClick(company);
  };

  if (loading) {
    return (
      <div className={styles.featuredSection}>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          }}
          open={loading}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{
              color: "#4eabe7",
            }}
          />
        </Backdrop>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.featuredSection}>
        <div className={styles.errorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.featuredSection}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <MedalIcon className={styles.titleIcon} />
          <h2 className={styles.titleText}>
            {t("components.featuredCompanies.header")}
          </h2>
        </div>
        <span className={styles.subtitle}>
          {t("components.featuredCompanies.subtitle")}
        </span>
        <hr className={styles.divider} />
      </div>
        {featuredCompanies.length === 0 && (
          <div className={styles.noResults}>
            <p>{t("components.featuredCompanies.noResults")}</p>
          </div>
        )}
      <div className={styles.companiesList}>
        {featuredCompanies.length > 0 &&
          featuredCompanies.map((company) => (
            <div
              key={company._id || company.id || company.nombreComercial}
              className={styles.companyCard}
            >
              <div className={styles.companyHeader}>
                <img
                  className={styles.companyBanner}
                  src={FeaturedCompanyBanner}
                  alt={t("common.bannerAlt")}
                />
                <AssetImage
                  src={company.logo || Caintra_Logo_03}
                  alt={t("common.companyLogoFor", {
                    name: company.nombreComercial,
                  })}
                  className={styles.companyLogo}
                />

              </div>
              <div className={styles.companyInfo}>
                <span className={styles.companyName}>
                  {company.nombreComercial}
                </span>
                <div className={styles.productTitleContainer}>
                  <span className={styles.productName}>
                    {company.sectorNombre || company.actividadGeneral || ""}
                  </span>
                </div>
                <div className={styles.viewProfileContainer} onClick={() => handleCompanySearch(company)}>
                  <span className={styles.viewProfileText}>
                    {t("components.featuredCompanies.visitProfile")}
                  </span>
                  <ChevronRightIcon
                    className={styles.icon}
                  />
                </div>
              </div>
            </div>
          ))}

      </div>

    </div>
  );
};

export default FeaturedCompanies;
